"use client";

import { useEffect } from "react";
import { m as motion } from "framer-motion";
import { ArrowDown, Check } from "lucide-react";
import { useLocale } from "next-intl";
import { ContattiHeroVisual } from "@/components/contatti/ContattiHeroVisual";
import { ContattiHeroGraphic } from "@/components/contatti/ContattiHeroGraphic";
import { trackEvent } from "@/lib/track";

const isBookingHash = (hash: string) =>
  hash === "#tidycal" || hash === "#calendly";

const getCalendlyTargetId = () =>
  typeof window !== "undefined" && window.innerWidth >= 1280
    ? "tidycal-desktop"
    : "tidycal-mobile";

export function ContattiHero() {
  const locale = useLocale();
  const isItalian = locale.startsWith("it");

  // Scroll-into-view quando si arriva con hash #tidycal / #calendly. Il
  // calendario desktop vive ora in una sezione full-width separata
  // (TidyCalDesktopSection), che si monta da sola sull'hash: qui scrolliamo
  // solo verso l'id corretto (desktop/mobile) con qualche retry.
  useEffect(() => {
    const handleHash = () => {
      if (!isBookingHash(window.location.hash)) return;
      const scrollToCalendar = () => {
        const target = document.getElementById(getCalendlyTargetId());
        if (!target) return false;
        const headerOffset = window.innerWidth >= 768 ? 96 : 84;
        const top =
          target.getBoundingClientRect().top + window.scrollY - headerOffset;
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
        return true;
      };
      if (scrollToCalendar()) return;
      window.setTimeout(scrollToCalendar, 200);
      window.setTimeout(scrollToCalendar, 500);
      window.setTimeout(scrollToCalendar, 950);
    };

    handleHash();
    window.addEventListener("hashchange", handleHash);
    return () => window.removeEventListener("hashchange", handleHash);
  }, []);

  const badges = isItalian
    ? ["Gratis", "30 minuti", "Senza impegno", "Rispondo entro 24h"]
    : ["Free", "30 minutes", "No commitment", "I reply within 24h"];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="text-left"
    >
      <span className="mb-4 block font-mono text-label text-primary">
        {isItalian ? "Contatti" : "Contact"}
      </span>
      <h1 className="page-hero-title mb-6">
        {isItalian ? (
          <>
            Dalla tua idea a un{" "}
            <span className="text-shine">piano chiaro</span>, in 30 minuti
          </>
        ) : (
          <>
            From your idea to a <span className="text-shine">clear plan</span>, in
            30 minutes
          </>
        )}
      </h1>
      <p className="text-body-lg text-muted-foreground mb-6 leading-relaxed max-w-2xl">
        {isItalian ? (
          <>
            Una call gratis e senza impegno: mi dici dove sei, io ti dico{" "}
            <span className="font-medium text-foreground">se e come</span>{" "}
            posso aiutarti, con tempi e prezzi chiari da subito.
            <span className="mt-2 block font-medium text-primary">
              Nessun commerciale: parli con me.
            </span>
          </>
        ) : (
          <>
            A free call, no strings attached: you tell me where you are, I tell
            you <span className="font-medium text-foreground">if and how</span> I
            can help, with clear timelines and prices from the start.
            <span className="mt-2 block font-medium text-primary">
              No salespeople: you talk with me.
            </span>
          </>
        )}
      </p>
      <p className="text-sm text-muted-foreground/80 mb-8">
        {isItalian
          ? "Bariano, Bergamo e provincia, da remoto in tutta Italia."
          : "Bariano, Bergamo and province, remote across Italy."}
      </p>

      {/* Badge di rassicurazione: sopra la CTA, cosi' non si sovrappongono alla
          linea verticale animata del pulsante "Prenota una call veloce". */}
      <ul className="mb-8 flex flex-wrap gap-2">
        {badges.map((badge) => (
          <li
            key={badge}
            className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] bg-surface/50 px-3 py-1.5 text-xs text-muted-foreground"
          >
            <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
            {badge}
          </li>
        ))}
      </ul>

      <ContattiHeroVisual isItalian={isItalian} />

      <div className="mt-8">
        <div className="relative inline-flex items-center">
          <button
            type="button"
            onClick={() => {
              trackEvent("book_call_click", {
                tool: "tidycal",
                location: "hero",
              });
              document
                .getElementById(getCalendlyTargetId())
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="inline-flex items-center gap-4 text-lg sm:text-2xl font-medium text-primary hover:text-primary/80 transition-colors"
            aria-label={
              isItalian
                ? "Vai alla sezione per prenotare una call"
                : "Jump to the call booking section"
            }
          >
            <motion.span
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 1.4, repeat: Infinity }}
              className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10"
            >
              <ArrowDown className="w-4 h-4" />
            </motion.span>
            {isItalian ? "Prenota una call veloce" : "Book a quick call"}
          </button>
        </div>
      </div>

      {/* Grafica: visualizza "dalla call a un piano chiaro" (desktop 2-col) */}
      <div className="mt-12 hidden xl:block">
        <ContattiHeroGraphic isItalian={isItalian} />
      </div>
    </motion.div>
  );
}
